'use client';

import { usePathname } from 'next/navigation';
import NProgress from 'nprogress';
import { useEffect, useRef } from 'react';

// 配置 NProgress
NProgress.configure({
  showSpinner: false,
  minimum: 0.08,
  easing: 'ease',
  speed: 200,
  trickleSpeed: 200,
});

export default function NavigationProgress() {
  const pathname = usePathname();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 页面加载完成时停止进度条并清除延迟计时器
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    NProgress.done();

    return () => {
      // 清理时停止进度条并清除计时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      NProgress.done();
    };
  }, [pathname]); // 移除 searchParams，只监听路径变化

  useEffect(() => {
    // 监听页面变化开始时延迟启动进度条（仅路径变化）
    const handleStart = (targetUrl?: string) => {
      // 清除之前的延迟计时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (targetUrl) {
        const currentPath = window.location.pathname;
        const targetPath = new URL(targetUrl, window.location.origin).pathname;
        // 只要路径发生变化就延迟1秒显示进度条（包括二级路由）
        if (currentPath !== targetPath) {
          timeoutRef.current = setTimeout(() => {
            NProgress.start();
            timeoutRef.current = null;
          }, 1000);
        }
      } else {
        // 如果没有目标URL，延迟显示进度条（如浏览器前进后退）
        timeoutRef.current = setTimeout(() => {
          NProgress.start();
          timeoutRef.current = null;
        }, 1000);
      }
    };

    // 在组件挂载时绑定事件
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (state, title, url) {
      const targetUrl = typeof url === 'string' ? url : undefined;
      handleStart(targetUrl);
      return originalPushState.call(this, state, title, url);
    };

    window.history.replaceState = function (state, title, url) {
      const targetUrl = typeof url === 'string' ? url : undefined;
      handleStart(targetUrl);
      return originalReplaceState.call(this, state, title, url);
    };

    // 监听浏览器前进后退
    const handlePopstate = () => handleStart();
    window.addEventListener('popstate', handlePopstate);

    // 监听链接点击
    const handleLinkClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (
        link &&
        link.href &&
        !link.href.startsWith('mailto:') &&
        !link.href.startsWith('tel:') &&
        !link.hasAttribute('data-download-link') // 忽略下载链接
      ) {
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);
        // 只有在路径变化时才显示进度条（忽略查询参数变化）
        if (url.pathname !== currentUrl.pathname) {
          handleStart(link.href);
        }
      }
    };

    document.addEventListener('click', handleLinkClick);

    return () => {
      // 清除延迟计时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // 恢复原始方法
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopstate);
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  return null;
}
